import React, { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'

import { formatDate, formatMoney, chequeNumber } from 'helpers/helpers'
import { loadComplaintBot, getComplaintBot, getComplaintBotUrl } from 'helpers/complaintBot'
import { drawingForCheque } from 'helpers/chequePrint'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ЮК ХАТИ (Отгрузка) — 80 mm, OMBORCHI chop etadi
 *  2026-08-26
 *
 *  Egasining talabi: SMZ zavodining «Отгрузка» hujjati andozasida, LEKIN
 *  80 mm printerga moslab + SUMMALAR bilan (90% holatda pulni HAYDOVCHI
 *  olib keladi) + sotuvchi tanlagan TO'LOV REJASI ko'rinib tursin.
 *
 *  Cheque80 dan farqi: bu SOTUV cheki emas, YUK HUJJATI — haydovchi bloki,
 *  imzo qatorlari va to'lov ramkasi bor. Ma'lumot manbai bitta:
 *  GET /cheques/{id} (Board.js -> printShipDocDirect).
 *
 *  ⚠️ 2026-09-01 — OMBORCHINING 4 TA TALABI (egasi orqali):
 *    1) RULON ko'rsatilsin — «tayyor»dan yozilganda ham faqat mahsulot nomi
 *       turardi, omborchi qaysi rulondan ekanini bilmasdi (rulon nomida rang
 *       ham bor: «9003/040 OQ RANG TMZ»);
 *    2) CHIZMA chiqsin — «har bir yukni qaysi mahsulot, qaysi chizma va qaysi
 *       rangdaligini bilib turishi kerak»;
 *    3) Har qatorga KATAKCHA (☐) — yuklanganini belgilab borish uchun
 *       («qaysi biri ketgan, qaysi qolgan»);
 *    4) BO'LIM bo'yicha ajratilsin (Профнастил / Аксессуар / Туникафон / Маҳсулот).
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Bo'lim sarlavhalari — Kengash ustunlari tartibida (ChequeWork bilan bir xil) */
const DEPT_LABEL = {
	4: 'ПРОФНАСТИЛ',
	3: 'АКСЕССУАР',
	5: 'ТУНУКАФОН',
	1: 'МАҲСУЛОТ',
}
const DEPT_ORDER = [4, 3, 5, 1]

/** To'lov rejasi yorlig'i — ChequeController::normalizePaymentPlan kodlari */
const PLAN_LABELS = {
	cash:   'НАҚД',
	half:   'ЯРМИНИ ТЎЛАЙДИ',
	nasiya: 'НАСИЯ',
	driver: 'ШОФЁРДАН',
}

/** Qator miqdori — mahsulot turiga mos birlikda (CLAUDE.md birlik qoidasi) */
function qtyText(p) {
	const type = Number(p.product_type)
	const prepared = !!p.is_prepared
	if (!prepared && (type === 3 || type === 4)) {
		return `${formatMoney(p.metr, 2)} м`
	}
	if (!prepared && type === 5) {
		// Tunikofon kv.metrda sotiladi (metr × eni)
		const w = Number(p.draw_width) > 0 ? Number(p.draw_width) : Number(p.width) || 0
		const kv = w > 0 ? Number(p.metr) * w : Number(p.metr)
		return `${formatMoney(kv, 2)} м²`
	}
	return `${formatMoney(p.quantity, 0)} дона`
}

function ChequeShipment80({ data = { products: [] }, printChequeRef, show = false }) {
	// Shikoyat QR i — Cheque80 bilan bir xil manba (localStorage sinxron)
	const [botName, setBotName] = useState(getComplaintBot())
	useEffect(() => {
		let alive = true
		loadComplaintBot().then(() => { if (alive) setBotName(getComplaintBot()) })
		return () => { alive = false }
	}, [])
	const botUrl = botName ? getComplaintBotUrl() : null

	/* Chop etayotgan odam (omborchi) — redux persist'idan. O'qib bo'lmasa
	   bo'sh qoladi, hujjat baribir chiqadi (imzo qatori bor). */
	let releasedBy = ''
	try {
		releasedBy = JSON.parse(localStorage.getItem('state'))?.account?.name || ''
	} catch (e) { /* jim — ixtiyoriy maydon */ }

	// To'liq qaytarilgan qatorlar yuk xatiga kirmaydi (Cheque80 qoidasi bilan bir xil)
	const rows = (data.products || []).filter(p =>
		Number(p.status ?? 1) > 0 &&
		!(Number(p.returned_quantity) > 0 && Number(p.returned_quantity) >= Number(p.quantity)))

	/* BO'LIM bo'yicha guruhlash (2026-09-01). Noma'lum tur oxirida «БОШҚА».
	   ⚠️ Raqam guruhlashdan KEYIN beriladi — qog'ozda yuqoridan pastga
	      1,2,3… bo'lib ketsin (avval qator tartibida raqamlanganda ekranda
	      «3,4 … 1,2» bo'lib chiqib, sanashda chalkashtirardi). */
	const seen = new Set()
	let no = 0
	const groups = [
		...DEPT_ORDER,
		...rows.map(p => Number(p.product_type)).filter(t => !DEPT_ORDER.includes(t)),
	]
		.filter(t => (seen.has(t) ? false : seen.add(t)))
		.map(type => ({ type, items: rows.filter(p => Number(p.product_type) === type) }))
		.filter(g => g.items.length > 0)
		.map(g => ({ ...g, items: g.items.map(p => ({ ...p, i: ++no })) }))

	const total    = Number(data.total_amount) || 0
	const discount = Number(data.discount_amount) || 0
	const paid     = Number(data.net_paid ?? data.paid) || 0
	const debt     = Number(data.net_cheque_debt ?? data.debt_amount) || 0
	const plan     = PLAN_LABELS[data.payment_plan] || null

	const line = { borderTop: '1px dashed #000', margin: '6px 0' }
	const row2 = { display: 'flex', justifyContent: 'space-between', gap: 6 }

	return (
		<div ref={printChequeRef}
			style={{
				width: '72mm', padding: '2mm', fontFamily: 'Arial, sans-serif',
				fontSize: 12, color: '#000', background: '#fff',
				display: show ? 'block' : undefined,
			}}>

			{/* ── Sarlavha: brend + hujjat nomi ── */}
			{data.logo &&
				<div style={{ textAlign: 'center' }}>
					<img src={`https://backend.tameer.uz/uploads/poses/${data.logo}`} alt=""
						style={{ maxWidth: '40mm', maxHeight: '14mm' }} />
				</div>}
			{data.pos_name &&
				<div style={{ textAlign: 'center', fontWeight: 700, fontSize: data.logo ? 13 : 15 }}>
					{data.pos_name}
				</div>}
			<div style={{ textAlign: 'center', fontWeight: 700, fontSize: 15, marginTop: 2 }}>
				ЮК ХАТИ (Отгрузка)
			</div>
			<div style={{ textAlign: 'center', fontSize: 22, fontWeight: 700 }}>
				№ {chequeNumber(data)}
			</div>
			<div style={{ textAlign: 'center', fontSize: 12 }}>
				{formatDate(data.cheque_opened_at || data.created_at, 'dd.MM.yyyy HH:mm')}
			</div>

			<div style={line} />

			{/* ── Kim / kimga / kim olib ketadi (SMZ tartibi) ── */}
			{releasedBy &&
				<div style={row2}><span>Товар отпустил:</span><b>{releasedBy}</b></div>}
			{data.cashier_name &&
				<div style={row2}><span>Сотувчи:</span><b>{data.cashier_name}</b></div>}
			{/* Mijoz ISMI kichik, TELEFONI katta (egasining talabi 2026-08-26):
			    omborchi/haydovchi telefonga qaraydi, ism ikkinchi darajali */}
			<div style={row2}>
				<span>Покупатель:</span>
				<span style={{ fontSize: 10.5 }}>{data.client_name || '—'}</span>
			</div>
			{data.client_phone &&
				<div style={{ ...row2, alignItems: 'baseline' }}>
					<span>Тел:</span>
					<b style={{ fontSize: 16 }}>{data.client_phone}</b>
				</div>}
			{/* Haydovchi — kiritilgan bo'lsagina (mijoz o'zi olib ketsa chiqmaydi) */}
			{data.driver_name &&
				<div style={row2}><span>Водитель:</span><b>{data.driver_name}</b></div>}
			{data.driver_phone &&
				<div style={row2}><span>Тел. водителя:</span><b>{data.driver_phone}</b></div>}
			{data.driver_car &&
				<div style={row2}><span>Номер машины:</span><b>{data.driver_car}</b></div>}

			<div style={line} />

			{/* ── Mahsulotlar: BO'LIM bo'yicha ajratilgan, har qatorda katakcha ──
			      Ustunlar: ☐ / № / nomi + rulon + chizma / soni / summa */}
			{groups.map(g => (
				<div key={g.type}>
					{/* Bo'lim sarlavhasi — bitta bo'limli chekda ham chiqadi:
					    omborchi qaysi sexdan kelganini bilishi kerak */}
					<div style={{
						background: '#000', color: '#fff', fontWeight: 700, fontSize: 12,
						padding: '1px 5px', marginTop: 5, letterSpacing: '.5px',
					}}>{DEPT_LABEL[g.type] || 'БОШҚА'}</div>

					<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
						<thead>
							<tr style={{ borderBottom: '1px solid #000' }}>
								<th style={{ width: 22 }}></th>
								<th style={{ textAlign: 'left', width: 14 }}>№</th>
								<th style={{ textAlign: 'left' }}>Номи / рулон</th>
								<th style={{ textAlign: 'right', width: 64 }}>Сони</th>
								<th style={{ textAlign: 'right', width: 72 }}>Сумма</th>
							</tr>
						</thead>
						<tbody>
							{g.items.map(p => {
								/* Chizma — omborchi uchun «worker» ko'rinishi: o'lchamlar,
								   burchaklar va RANG TOMONI strelkasi bilan (u qaysi tomon
								   bo'yalganini ko'rib yuklashi kerak). */
								const drawing = drawingForCheque(p, 'worker')
								const roll = p.element_name || null
								return (
									<tr key={p.i} style={{ borderBottom: '1px dotted #999', verticalAlign: 'top' }}>
										{/* KATAKCHA — ptichka qo'yish uchun (yuklandi/qoldi) */}
										<td style={{ paddingTop: 3 }}>
											<span style={{
												display: 'inline-block', width: 15, height: 15,
												border: '2px solid #000', borderRadius: 2,
											}} />
										</td>
										<td style={{ paddingTop: 3 }}>{p.i}</td>
										<td style={{ paddingRight: 4, paddingTop: 3 }}>
											<div style={{ fontWeight: 700 }}>{p.name || p.product_name}</div>
											{/* RULON = mahsulot qaysi rulondan; nomida RANG ham bor */}
											{roll && <div style={{ fontSize: 11.5 }}>рулон: {roll}</div>}
											{drawing &&
												<img src={drawing} alt=""
													style={{ display: 'block', width: '100%', maxWidth: '34mm', margin: '2px 0 3px' }} />}
										</td>
										<td style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingTop: 3 }}>{qtyText(p)}</td>
										<td style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingTop: 3 }}>
											{formatMoney(Math.round(Number(p.total_price) || 0))}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			))}

			{/* ── Jamlanma ── */}
			<div style={{ ...row2, fontSize: 15, fontWeight: 700, marginTop: 4 }}>
				<span>ЖАМИ:</span><span>{formatMoney(Math.round(total + discount))}</span>
			</div>
			{discount > 0 &&
				<div style={row2}><span>Чегирма:</span><span>−{formatMoney(Math.round(discount))}</span></div>}
			{discount > 0 &&
				<div style={{ ...row2, fontWeight: 700 }}><span>Тўловга:</span><span>{formatMoney(Math.round(total))}</span></div>}
			{paid > 0 &&
				<div style={row2}><span>Тўланган:</span><span>{formatMoney(Math.round(paid))}</span></div>}

			{/* ── TO'LOV RAMKASI — egasining talabi: to'lov rejasi ko'rinib
			      tursin; «Шофёрдан»da haydovchi olib keladigan summa KATTA ── */}
			{plan &&
				<div style={{
					border: '2px solid #000', borderRadius: 4, padding: '4px 6px',
					marginTop: 6, textAlign: 'center',
				}}>
					<div style={{ fontWeight: 700, fontSize: 13 }}>ТЎЛОВ: {plan}</div>
					{debt > 0 && data.payment_plan === 'driver' && <>
						<div style={{ fontSize: 11, marginTop: 2 }}>Шофёр олиб келади:</div>
						<div style={{ fontSize: 20, fontWeight: 700 }}>{formatMoney(Math.round(debt))} сўм</div>
					</>}
					{debt > 0 && data.payment_plan !== 'driver' &&
						<div style={{ fontSize: 13, marginTop: 2 }}>Қарз: <b>{formatMoney(Math.round(debt))} сўм</b></div>}
				</div>}
			{/* Reja tanlanmagan eski cheklarda ham qarz ko'rinsin */}
			{!plan && debt > 0 &&
				<div style={{ ...row2, fontWeight: 700, marginTop: 4 }}>
					<span>Қарз:</span><span>{formatMoney(Math.round(debt))} сўм</span>
				</div>}

			<div style={line} />

			{/* ── Izoh + imzolar (SMZ tartibi) ── */}
			<div style={{ fontSize: 12, marginTop: 4 }}>
				Примечание: {data.note || '________________________'}
			</div>
			<div style={{ ...row2, marginTop: 14 }}>
				<span>Отпустил: ____________</span>
			</div>
			<div style={{ ...row2, marginTop: 12, marginBottom: 4 }}>
				<span>Принял: &nbsp;&nbsp;____________</span>
			</div>

			{/* ── Shikoyat QR (Cheque80 bilan bir xil) ── */}
			{botUrl &&
				<div style={{ textAlign: 'center', marginTop: 8 }}>
					<div style={{ fontWeight: 700, fontSize: 12 }}>ТАКЛИФ ВА ШИКОЯТ</div>
					<div style={{ fontSize: 10 }}>Фикрингиз биз учун муҳим</div>
					<div style={{ background: '#fff', display: 'inline-block', padding: 4 }}>
						<QRCodeSVG value={botUrl} size={110} />
					</div>
					<div style={{ fontSize: 11 }}>@{botName}</div>
				</div>}
		</div>
	)
}

export default ChequeShipment80
